import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  updatePassword,
  deleteUser
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { User, UserRole, Permission, UserProfile, UserSettings } from '@/types';

const googleProvider = new GoogleAuthProvider();

// Auth service class
export class AuthService {
  // Sign in with email and password
  static async signInWithEmail(email: string, password: string): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = await this.getUserData(userCredential.user.uid);
      return user;
    } catch (error) {
      throw new Error(`Sign in failed: ${error}`);
    }
  }

  // Sign up with email and password
  static async signUpWithEmail(
    email: string,
    password: string,
    userData: Partial<UserProfile>
  ): Promise<User> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Update Firebase Auth profile
      await updateProfile(firebaseUser, {
        displayName: `${userData.firstName} ${userData.lastName}`,
        photoURL: userData.avatar
      });

      // Create user document in Firestore
      const user: User = {
        id: firebaseUser.uid,
        role: UserRole.CLIENT, // Default role
        permissions: this.getDefaultPermissions(UserRole.CLIENT),
        settings: this.getDefaultSettings(),
        profile: {
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: firebaseUser.email || '',
          phone: userData.phone || '',
          avatar: userData.avatar || null,
          bio: userData.bio || '',
          company: userData.company || '',
          licenseNumber: userData.licenseNumber || ''
        },
        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any,
        lastLogin: new Date() as any,
        status: 'active' as any,
        credits: 5,
        usage: { totalCalls: 0, totalTokens: 0 },
        email: firebaseUser.email || ''
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), user);
      return user;
    } catch (error) {
      throw new Error(`Sign up failed: ${error}`);
    }
  }

  // Sign in with Google
  static async signInWithGoogle(): Promise<User> {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;

      // Check if user exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        return userDoc.data() as User;
      } else {
        // Create new user document
        const user: User = {
          id: firebaseUser.uid,
          role: UserRole.CLIENT,
          permissions: this.getDefaultPermissions(UserRole.CLIENT),
          settings: this.getDefaultSettings(),
          profile: {
            firstName: firebaseUser.displayName?.split(' ')[0] || '',
            lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
            email: firebaseUser.email || '',
            phone: '',
            avatar: firebaseUser.photoURL || null,
            bio: '',
            company: '',
            licenseNumber: ''
          },
          createdAt: serverTimestamp() as any,
          updatedAt: serverTimestamp() as any,
        lastLogin: new Date() as any,
          status: 'active' as any,
          credits: 5,
          usage: { totalCalls: 0, totalTokens: 0 },
          email: firebaseUser.email || ''
        };

        await setDoc(doc(db, 'users', firebaseUser.uid), user);
        return user;
      }
    } catch (error) {
      throw new Error(`Google sign in failed: ${error}`);
    }
  }

  // Sign out
  static async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      throw new Error(`Sign out failed: ${error}`);
    }
  }

  // Get current user data
  static async getCurrentUser(): Promise<User | null> {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        unsubscribe();
        if (firebaseUser) {
          try {
            const user = await this.getUserData(firebaseUser.uid);
            resolve(user);
          } catch (error) {
            console.error('Error getting user data:', error);
            resolve(null);
          }
        } else {
          resolve(null);
        }
      });
    });
  }

  // Get user data from Firestore
  static async getUserData(uid: string): Promise<User> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      return userDoc.data() as User;
    } catch (error) {
      throw new Error(`Failed to get user data: ${error}`);
    }
  }

  // Update user profile
  static async updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const updateData: any = {};
      Object.entries(updates).forEach(([key, value]) => {
        updateData[`profile.${key}`] = value;
      });
      
      await updateDoc(doc(db, 'users', uid), {
        ...updateData,
        updatedAt: new Date()
      });
    } catch (error) {
      throw new Error(`Failed to update profile: ${error}`);
    }
  }

  // Update user settings
  static async updateUserSettings(uid: string, settings: Partial<UserSettings>): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', uid), {
        'settings': settings,
        updatedAt: new Date()
      });
    } catch (error) {
      throw new Error(`Failed to update settings: ${error}`);
    }
  }

  // Update user role and permissions
  static async updateUserRole(uid: string, role: UserRole, permissions: Permission[]): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', uid), {
        role,
        permissions,
        updatedAt: new Date()
      });
    } catch (error) {
      throw new Error(`Failed to update user role: ${error}`);
    }
  }

  // Reset password
  static async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw new Error(`Password reset failed: ${error}`);
    }
  }

  // Update password
  static async updateUserPassword(newPassword: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No user signed in');
      await updatePassword(user, newPassword);
    } catch (error) {
      throw new Error(`Password update failed: ${error}`);
    }
  }

  // Delete user account
  static async deleteUserAccount(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No user signed in');
      
      // Delete user document from Firestore
      await deleteDoc(doc(db, 'users', user.uid));
      
      // Delete Firebase Auth user
      await deleteUser(user);
    } catch (error) {
      throw new Error(`Account deletion failed: ${error}`);
    }
  }

  // Get users by role
  static async getUsersByRole(role: UserRole): Promise<User[]> {
    try {
      const q = query(collection(db, 'users'), where('role', '==', role));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => doc.data() as User);
    } catch (error) {
      throw new Error(`Failed to get users by role: ${error}`);
    }
  }

  // Check if user has permission
  static hasPermission(user: User, permission: Permission): boolean {
    return user.permissions.includes(permission);
  }

  // Get default permissions for role
  static getDefaultPermissions(role: UserRole): Permission[] {
    switch (role) {
      case UserRole.ADMIN:
        return Object.values(Permission);
      case UserRole.AGENT:
        return [
          Permission.READ_PROPERTIES,
          Permission.WRITE_PROPERTIES,
          Permission.DELETE_PROPERTIES,
          Permission.READ_CLIENTS,
          Permission.WRITE_CLIENTS,
          Permission.READ_ANALYTICS
        ];
      case UserRole.PROMOTEUR:
        return [
          Permission.READ_PROPERTIES,
          Permission.WRITE_PROPERTIES,
          Permission.DELETE_PROPERTIES,
          Permission.READ_ANALYTICS
        ];
      case UserRole.CLIENT:
        return [
          Permission.READ_PROPERTIES
        ];
      default:
        return [];
    }
  }

  // Get default user settings
  private static getDefaultSettings(): UserSettings {
    return {
      theme: 'auto',
      language: 'en',
      notifications: {
        email: true,
        push: true,
        sms: false,
        propertyMatches: true,
        clientUpdates: true,
        systemAlerts: true
      },
      dashboard: {
        defaultView: 'grid',
        itemsPerPage: 12,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      }
    };
  }
}

// Auth state hook
export const useAuthState = () => {
  return new Promise<User | null>((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      unsubscribe();
      if (firebaseUser) {
        try {
          const user = await AuthService.getUserData(firebaseUser.uid);
          resolve(user);
        } catch (error) {
          console.error('Error getting user data:', error);
          resolve(null);
        }
      } else {
        resolve(null);
      }
    });
  });
};
